import { useState } from "react";
import { PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

export const INTERESTS = [
    "Exploring healthcare options (Patient)",
    "Hospital Partnership",
    "Investment",
    "Strategic Partnership",
    "Technology",
    "Other",
];

const initial = { name: "", email: "", country: "", organization: "", interest: "", message: "", company_website: "" };

export default function InquiryForm({ defaultInterest = "", testidPrefix = "inquiry" }) {
    const [form, setForm] = useState({ ...initial, interest: defaultInterest });
    const [submitting, setSubmitting] = useState(false);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.interest) {
            toast.error("Please select what you are interested in.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/inquiries", form);
            toast.success("Thank you — your inquiry has been received. Our team will reach out soon.");
            setForm({ ...initial, interest: defaultInterest });
        } catch (err) {
            const detail = err.response?.data?.detail;
            const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join(" ") : detail;
            toast.error(msg || "Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form data-testid={`${testidPrefix}-form`} onSubmit={submit} className="space-y-6">
            {/* honeypot — hidden from humans */}
            <div className="absolute -left-[9999px] top-0" aria-hidden="true">
                <input
                    type="text"
                    name="company_website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.company_website}
                    onChange={set("company_website")}
                />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                    <Label htmlFor={`${testidPrefix}-name`}>Name *</Label>
                    <Input data-testid={`${testidPrefix}-name-input`} id={`${testidPrefix}-name`} required minLength={2} placeholder="Your full name" value={form.name} onChange={set("name")} className="rounded-xl h-12" />
                </div>
                <div className="space-y-2">
                    <Label htmlFor={`${testidPrefix}-email`}>Email *</Label>
                    <Input data-testid={`${testidPrefix}-email-input`} id={`${testidPrefix}-email`} type="email" required placeholder="you@example.com" value={form.email} onChange={set("email")} className="rounded-xl h-12" />
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                    <Label htmlFor={`${testidPrefix}-country`}>Country *</Label>
                    <Input data-testid={`${testidPrefix}-country-input`} id={`${testidPrefix}-country`} required minLength={2} placeholder="Where are you based?" value={form.country} onChange={set("country")} className="rounded-xl h-12" />
                </div>
                <div className="space-y-2">
                    <Label htmlFor={`${testidPrefix}-organization`}>Organization</Label>
                    <Input data-testid={`${testidPrefix}-organization-input`} id={`${testidPrefix}-organization`} placeholder="Hospital, company or institution" value={form.organization} onChange={set("organization")} className="rounded-xl h-12" />
                </div>
            </div>

            <div className="space-y-2">
                <Label>I am interested in: *</Label>
                <Select value={form.interest} onValueChange={(v) => setForm((f) => ({ ...f, interest: v }))}>
                    <SelectTrigger data-testid={`${testidPrefix}-interest-select`} className="rounded-xl h-12">
                        <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                        {INTERESTS.map((opt) => (
                            <SelectItem key={opt} value={opt} data-testid={`${testidPrefix}-interest-option-${opt.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                                {opt}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="space-y-2">
                <Label htmlFor={`${testidPrefix}-message`}>Message *</Label>
                <Textarea data-testid={`${testidPrefix}-message-input`} id={`${testidPrefix}-message`} required minLength={5} rows={5} placeholder="Tell us a little about what you're looking for…" value={form.message} onChange={set("message")} className="rounded-xl" />
            </div>

            <button
                data-testid={`${testidPrefix}-submit-btn`}
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 rounded-full bg-brand-blue px-8 py-4 text-sm font-semibold text-white shadow-lg shadow-brand-blue/25 hover:bg-brand-blue-light active:scale-95 disabled:opacity-60 disabled:pointer-events-none transition-[background-color,box-shadow,transform] duration-200"
            >
                <PaperPlaneTilt size={16} weight="bold" />
                {submitting ? "Sending…" : "Send Inquiry"}
            </button>
        </form>
    );
}
